import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { ErrorMessageComponent } from "@/Common/Components/Errors/ErrorMessageComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { PortalUserWithPermissionsMyleDto, SharedPatientDto, UserPermissionFlags } from "@/Common/Models/Portal/MedfarPortal";
import { SharedPatientMyleDto } from "@/Common/Models/Portal/Patient/SharedPatientMyleDto";
import { PortalAccountChanged } from "@/Common/Models/Portal/PortalAccountChanged";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { IPortalManagementPopupErrorFunction, LinkMylePatientToPortalAccountDialog } from "./LinkMylePatientToPortalAccountDialog";

interface IAccountUserSharingProps {
    portalUser: PortalUserWithPermissionsMyleDto;
    patientContactId: string;
    areControlsDisabled?: boolean;
}

class AccountUserSharingState {
    public errors: string[];
    public isLinkPopupOpen: boolean;
    public sharedAccounts: SharedPatientMyleDto[];
}

class AccountUserSharing extends React.Component<IAccountUserSharingProps, AccountUserSharingState> {
    constructor(props: IAccountUserSharingProps) {
        super(props);
        this.state = new AccountUserSharingState();
    }

    public componentDidMount(): void {
        if (this.props.portalUser != null) {
            this.setState({
                sharedAccounts: this.buildSharedAccounts(this.props.portalUser.SharedPatients)
            });
        }
    }

    public componentDidUpdate(prevProps: IAccountUserSharingProps): void {
        const prevSharedPatients = prevProps.portalUser?.SharedPatients;
        const currentSharedPatients = this.props.portalUser?.SharedPatients;

        if (this.props.portalUser != null && currentSharedPatients !== prevSharedPatients) {
            this.setState({
                sharedAccounts: this.buildSharedAccounts(currentSharedPatients)
            });
        }
    }

    public render(): JSX.Element {
        let self = this;
        let result: JSX.Element = null;
        let sharedAccounts: SharedPatientMyleDto[] = this.state.sharedAccounts;
        let dataChanged = this.hasDataChanged();

        // remove current user from shared accounts if its there
        if (sharedAccounts != null && sharedAccounts.length > 0) {
            sharedAccounts = sharedAccounts.filter(x => x.MyleId != this.props.patientContactId);
        }

        let cssClass = "centered";
        let limitHeightForPopupDisplay: React.CSSProperties = {};
        if (this.props.areControlsDisabled) {
            cssClass = "";
            limitHeightForPopupDisplay.maxHeight = "130px";
            limitHeightForPopupDisplay.overflowY = "auto";
        }

        let hasSharedAccount = (sharedAccounts != null && sharedAccounts.length > 0);

        result = (
            <div>

                {this.state.isLinkPopupOpen && (
                    <LinkMylePatientToPortalAccountDialog
                        patientContactId={self.props.patientContactId}
                        componentId="patientPortal_sub_linkMemberAccount"
                        onCloseCallback={self.onClosePopup.bind(self)}
                        onAddFunction={this.onAttachUser.bind(this)}
                    />
                )}

                <div className="block separated">
                    <div className="flex-container heading">
                        <h3 className="thin-font">
                            {this.props.areControlsDisabled
                                ? cacheStore.GetString("AccessRightsLabel")
                                : cacheStore.GetString("AccessRightsLabel") + ": " + cacheStore.GetString("HasAccessToLabel")
                            }
                        </h3>
                        {!this.props.areControlsDisabled && (
                            <div>
                                <div className="flex-container">
                                    <button onClick={this.displayAddSharedAccountDialog.bind(this)} className="btn btn-primary push-right-5">
                                        <span>{cacheStore.GetString("AddLabel")}</span>
                                    </button>

                                    <button disabled={!dataChanged} onClick={this.saveChanges.bind(this)} className="btn btn-primary push-right-5">
                                        <span>{cacheStore.GetString("SaveLabel")}</span>
                                    </button>
                                    <button disabled={!dataChanged} onClick={this.undoChanges.bind(this)} className="btn btn-primary push-right-5">
                                        <span>{cacheStore.GetString("UndoChangesLabel")}</span>
                                    </button>

                                </div>
                            </div>
                        )}

                    </div>
                    <div className={cssClass} style={limitHeightForPopupDisplay}>

                        <ErrorMessageComponent errors={this.state.errors} />

                        {!hasSharedAccount &&
                            <div className="flex-container center padding-bottom-10"> {cacheStore.GetString("NoSharedAccountLabel")} </div>
                        }

                        {hasSharedAccount && !this.props.areControlsDisabled && sharedAccounts != null && sharedAccounts.length > 0 && (
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th className="center" />
                                        <th className="center">{cacheStore.GetString("EMRLabel")}</th>
                                        <th className="center">{cacheStore.GetString("DocumentsLabel")}</th>
                                        <th className="center">{cacheStore.GetString("MessagesLabel")}</th>
                                        <th className="center">{cacheStore.GetString("ProfileInformationLabel")}</th>
                                        <th className="center">{cacheStore.GetString("AppointmentsLabel")}</th>
                                        <th className="center">{cacheStore.GetString("BillingLabel")}</th>
                                        <th className="center" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {sharedAccounts.map((account, i) => {
                                        let isCurrentUser = account.MyleId == this.props.patientContactId;

                                        return (
                                            <tr key={"accountUserSharing_user_" + i}>
                                                <td className="center">{account.FullName}</td>
                                                <td className="center"> <CheckboxControl disabled={isCurrentUser} onChange={this.setSharingForUser.bind(this, account, UserPermissionFlags.Dossier)} checked={this.hasFlag(account.PermissionFlags, UserPermissionFlags.Dossier)} /> </td>
                                                <td className="center"> <CheckboxControl disabled={isCurrentUser} onChange={this.setSharingForUser.bind(this, account, UserPermissionFlags.Document)} checked={this.hasFlag(account.PermissionFlags, UserPermissionFlags.Document)} /> </td>
                                                <td className="center"> <CheckboxControl disabled={isCurrentUser} onChange={this.setSharingForUser.bind(this, account, UserPermissionFlags.Message)} checked={this.hasFlag(account.PermissionFlags, UserPermissionFlags.Message)} /> </td>
                                                <td className="center"> <CheckboxControl disabled={isCurrentUser} onChange={this.setSharingForUser.bind(this, account, UserPermissionFlags.Profile)} checked={this.hasFlag(account.PermissionFlags, UserPermissionFlags.Profile)} /> </td>
                                                <td className="center"> <CheckboxControl disabled={isCurrentUser} onChange={this.setSharingForUser.bind(this, account, UserPermissionFlags.Appointment)} checked={this.hasFlag(account.PermissionFlags, UserPermissionFlags.Appointment)} /> </td>
                                                <td className="center"> <CheckboxControl disabled={isCurrentUser} onChange={this.setSharingForUser.bind(this, account, UserPermissionFlags.Billing)} checked={this.hasFlag(account.PermissionFlags, UserPermissionFlags.Billing)} /> </td>
                                                {isCurrentUser == false &&
                                                    <td className="center"> <span onClick={this.removeSharedAccountFromCurrent.bind(this, account)} className="red-x"> X  </span></td>
                                                }
                                                {isCurrentUser &&
                                                    <td className="center"> <span className="red-x">   </span></td>
                                                }
                                            </tr>
                                        );
                                    })}

                                </tbody>
                            </table>
                        )}
                        {hasSharedAccount && this.props.areControlsDisabled && (
                            <table className="table" style={{ tableLayout: "fixed" }}>
                                <thead>
                                    <tr>
                                        <th className="center">{cacheStore.GetString("HasAccessToLabel")}</th>
                                        <th className="center">{cacheStore.GetString("SharedInformationLabel")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sharedAccounts.map((account, i) => (
                                        <tr key={"accountUserSharing_user_" + i}>
                                            <td className="center"> <strong>{account.FullName} </strong> </td>
                                            <td className="center"> {this.getSharedInformationString(account)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        );

        return result;
    }

    private setSharingForUser(sharedAccount: SharedPatientMyleDto, permission: UserPermissionFlags, checkedValue: boolean): void {
        let state = this.state;
        let idx = state.sharedAccounts.indexOf(sharedAccount);

        if (checkedValue)
            state.sharedAccounts[idx].PermissionFlags |= permission; // bitshift this will add a flag
        else
            state.sharedAccounts[idx].PermissionFlags &= ~permission; // bitshift this will clear a flag

        this.setState(state);
    }

    private displayAddSharedAccountDialog(): void {
        let s = new AccountUserSharingState();
        s.isLinkPopupOpen = true;
        this.setState(s);
    }

    private onClosePopup(): void {
        let s = new AccountUserSharingState();
        s.isLinkPopupOpen = false;
        this.setState(s);
    }

    private hasFlag(value: UserPermissionFlags, flagToCheck: UserPermissionFlags): boolean {
        let enumVal: any = flagToCheck;
        if (value & enumVal)
            return true;
        else
            return false;
    }

    private getSharedInformationString(sharedAccount: SharedPatientDto): string {
        var results: string[] = [];

        if (this.hasFlag(sharedAccount.PermissionFlags, UserPermissionFlags.Appointment))
            results.push(cacheStore.GetString("AppointmentsLabel"));
        if (this.hasFlag(sharedAccount.PermissionFlags, UserPermissionFlags.Document))
            results.push(cacheStore.GetString("DocumentsLabel"));
        if (this.hasFlag(sharedAccount.PermissionFlags, UserPermissionFlags.Dossier))
            results.push(cacheStore.GetString("EMRLabel"));
        if (this.hasFlag(sharedAccount.PermissionFlags, UserPermissionFlags.Profile))
            results.push(cacheStore.GetString("ProfileInformationLabel"));
        if (this.hasFlag(sharedAccount.PermissionFlags, UserPermissionFlags.Billing))
            results.push(cacheStore.GetString("BillingLabel"));

        return results.join(", ");
    }

    private onAttachUser(newMemberMyleId: string, onAttachError: IPortalManagementPopupErrorFunction, person: Person): void {
        // add if not already in list
        if (!this.state.sharedAccounts.some(x => x.MyleId == newMemberMyleId)) {
            let newShared = SharedPatientMyleDto.fromPerson(person);
            let accounts = this.state.sharedAccounts.slice();
            accounts.push(newShared);
            var s = new AccountUserSharingState();
            s.sharedAccounts = accounts;
            s.isLinkPopupOpen = false;
            this.setState(s);
        }
    }

    private removeSharedAccountFromCurrent(sharedAccount: SharedPatientMyleDto): void {
        if (sharedAccount != null) {
            let idx = this.state.sharedAccounts.indexOf(sharedAccount);

            if (idx != -1) {
                let accounts = this.state.sharedAccounts.slice();
                if (accounts.length == 1)
                    accounts = [];
                else
                    accounts.splice(idx, 1);

                let s = new AccountUserSharingState();
                s.sharedAccounts = accounts;
                this.setState(s);
            }
        }
    }

    private hasDataChanged(): boolean {
        if (!this.props.portalUser?.SharedPatients || !this.state.sharedAccounts)
            return false;

        if (this.props.portalUser.SharedPatients.length !== this.state.sharedAccounts.length)
            return true;

        for (let i = 0; i < this.props.portalUser.SharedPatients.length; i++) {
            if (!SharedPatientMyleDto.areEqual(this.props.portalUser.SharedPatients[i], this.state.sharedAccounts[i])) {
                return true;
            }
        }

        return false;
    }

    private saveChanges(): void {
        let client = new Client();

        let requestData = {
            PatientProfileId: this.props.patientContactId,
            SharedPatientProfiles: this.state.sharedAccounts
        };

        client.Post(apiSettings.buildApiPath(), ServerOperations.Server.Portal_Proxy_Account_UpdateSharedAccount, requestData, response => this.onSaveChangesSuccess(response), err => this.onError(err));
    }

    private onSaveChangesSuccess(response: { Errors: ErrorMessage[], PortalAccountsChanged: PortalAccountChanged[] }): void {
        let errors = response.Errors;

        if (errors == null || errors.length == 0) {
            this.updatePatientCacheForModifiedSharedAccounts(response.PortalAccountsChanged);

            // reload portal patient account section
            const op = new LocalEvent(LocalOperations.Portal.Account_Load);
            op.AddItem("patientContactId", this.props.patientContactId);
            LocalEventDispatcher.dispatch(op);
        }
    }

    private updatePatientCacheForModifiedSharedAccounts(portalAccountChanged: PortalAccountChanged[]): void {
        if (portalAccountChanged && portalAccountChanged.length) {
            portalAccountChanged.forEach(a => this.updateCacheForPatient(a));
        }
    }

    private updateCacheForPatient(portalAccountChanged: PortalAccountChanged): void {
        if (patientStore.PatientModels[portalAccountChanged.PatientContactId]) {
            let patientModel = patientStore.PatientModels[portalAccountChanged.PatientContactId];
            if (this.WasChildStatusReset(portalAccountChanged.PortalRegistrationState) ||
                this.WasChildStatusAdded(patientModel)) {
                patientStore.updatePatientPortalInfo(
                    portalAccountChanged.PatientContactId,
                    portalAccountChanged.PortalPatientProfileId,
                    portalAccountChanged.PortalRegistrationState,
                    null
                );
            }
        }
    }

    private WasChildStatusReset(portalRegistrationState: PortalRegistrationState): boolean {
        return !portalRegistrationState;
    }

    private WasChildStatusAdded(patientModel: Patient): boolean {
        return patientModel.IndividualResponse != null
            && (
                !patientModel.IndividualResponse.PortalSyncState
                || patientModel.IndividualResponse.PortalSyncState == PortalRegistrationState.Default
            );
    }

    private undoChanges(): void {
        this.componentWillMount();
    }

    private onError(err: string): void {
        let self = this;
        var s = new AccountUserSharingState();
        s.errors = [err];
        self.setState(s);
    }

    private buildSharedAccounts(sharedPatients: SharedPatientMyleDto[]): SharedPatientMyleDto[] {
        return sharedPatients.map(p => {
            // Deep copy
            const sharedPatientClone: SharedPatientMyleDto = $.extend(true, {}, p);

            if (sharedPatientClone.PermissionFlags === UserPermissionFlags.FullControl) {
                sharedPatientClone.PermissionFlags = PortalHelper.getAllSharedPatientPermissionFlags();
            }
            return sharedPatientClone;
        });
    }
}

export { AccountUserSharing };
